import { useCallback, useEffect, useMemo, useState } from 'react';
import { NeonGrid, useTheme } from 'neon-design-system';
import {
  ColDef,
  GridReadyEvent,
  GridApi,
  IServerSideDatasource,
  IComponent,
  FirstDataRenderedEvent,
  GetRowNodeIdFunc,
  ProcessRowParams,
  IsGroupOpenByDefaultParams,
  SelectionChangedEvent,
  RowNode
} from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { config } from '../../config';
import * as _ from 'lodash';
import {
  DEFAULT_COL_DEF,
  getVisibleColumns,
  onColumnVisible,
  setInitialHideColumns,
  addHeaderTooltip,
  clearColumnsSize,
  addSortable,
  getColumnState,
  saveColumnState,
  GroupCellRenderer,
  GroupCellRendererExtended
} from '../../utils/grid';
import { GroupCellRendererParams } from 'ag-grid-community/dist/lib/rendering/cellRenderers/groupCellRenderer';
import { ColumnState } from 'ag-grid-community/dist/lib/columnController/columnController';
LicenseManager.setLicenseKey(config.agGrid.license);

interface PageGridProps {
  pageName?: string;
  cols: ColDef[];
  serverSideDatasource?: () => IServerSideDatasource;
  rowData: any[] | undefined;
  paginationPageSize?: number;
  currentPage?: number;
  cacheFieldToInvalidateOnLoad?: string;
  components?: { [key: string]: new () => IComponent<GroupCellRendererParams> };
  suppressLoadingOverlay?: boolean;
  getRowNodeId?: GetRowNodeIdFunc;
  processRowPostCreate?: (params: ProcessRowParams) => void;
  onSelectionChanged?: (event: SelectionChangedEvent) => void;
  groupSelectsChildren?: boolean;
  suppressRowClickSelection?: boolean;
  isRowSelectable?: (node: RowNode) => boolean;
  domLayout?: 'normal' | 'autoHeight';
  rowHeight?: number;
  onSortChanged?: (columnState: ColumnState[]) => void;
  isGroupOpenByDefault?: (params: IsGroupOpenByDefaultParams) => boolean;
  overlayNoRowsTemplate?: string;
}

export const PageGrid = (props: PageGridProps) => {
  const {
    rowData,
    paginationPageSize,
    currentPage,
    cols,
    pageName = '',
    serverSideDatasource,
    suppressLoadingOverlay = true,
    getRowNodeId,
    processRowPostCreate,
    groupSelectsChildren = false,
    suppressRowClickSelection = true,
    onSelectionChanged,
    isRowSelectable,
    domLayout = 'autoHeight',
    rowHeight = 32,
    onSortChanged,
    components,
    isGroupOpenByDefault,
    overlayNoRowsTemplate
  } = props;
  const [gridApi, setGridApi] = useState<GridApi | undefined>();
  const [rowDataPaginated, setRowDataPaginated] = useState<any[] | undefined>();
  const visibleColumns = useMemo(() => getVisibleColumns(pageName), [pageName]);
  const columnState = useMemo(() => getColumnState(pageName), [pageName]);
  const { theme } = useTheme();

  const onGridReady = (e: GridReadyEvent) => {
    setGridApi(e.api);
  };

  useEffect(() => {
    if (gridApi && _.isNumber(currentPage)) {
      gridApi.paginationGoToPage(currentPage - 1);
    }
  }, [currentPage, gridApi]);

  useEffect(() => {
    if (gridApi && _.isNumber(paginationPageSize)) {
      // 0 = Show All, so set the page size to the length of the data. Setting it to 0 causes ag-grid to use it's default value of 100
      gridApi.paginationSetPageSize(paginationPageSize === 0 ? rowData?.length ?? 0 : paginationPageSize);
      gridApi.paginationGoToPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationPageSize, gridApi]);

  useEffect(() => {
    const isPaginationActive = _.isNumber(paginationPageSize) && _.isNumber(currentPage);
    const isPaginationDataMissed = _.isUndefined(paginationPageSize) && _.isUndefined(currentPage);

    if (gridApi && (isPaginationActive || isPaginationDataMissed)) {
      setRowDataPaginated(rowData);
    }
  }, [rowData, currentPage, paginationPageSize, gridApi]);

  const divAttrs = { style: { flexGrow: 1, marginBottom: theme.spacing(2.5) } };

  let columnDefs = useMemo(() => addSortable(addHeaderTooltip(setInitialHideColumns(cols, visibleColumns))), [cols, visibleColumns]);

  if (!pageName) {
    columnDefs = cols;
  }

  const handleFirstDataRender = useCallback(
    (e: FirstDataRenderedEvent) => {
      if (columnState && pageName) {
        e.columnApi.setColumnState(columnState);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnState]
  );

  const serverOrClientSettings = () => {
    // eslint-disable-next-line eqeqeq
    return serverSideDatasource != undefined && serverSideDatasource()
      ? {
          gridOptions: {
            getRowNodeId: getRowNodeId,
            rowModelType: 'serverSide',
            serverSideDatasource: serverSideDatasource!(),
            columnDefs: columnDefs
          }
        }
      : {
          columnDefs: columnDefs,
          rowData: rowDataPaginated,
          getRowNodeId: getRowNodeId
        };
  };

  return (
    <div {...divAttrs}>
      <NeonGrid
        {...serverOrClientSettings()}
        rowModelType={undefined}
        columnDefs={serverSideDatasource ? undefined : columnDefs}
        onSortChanged={(params) => {
          const columnState = params.columnApi.getColumnState();

          if (onSortChanged) onSortChanged(columnState);
          if (pageName) saveColumnState(pageName, columnState);
        }}
        defaultColDef={DEFAULT_COL_DEF}
        suppressMovableColumns
        groupSuppressAutoColumn
        getRowHeight={(p: any) => (p.node.group ? 36 : rowHeight)}
        suppressLoadingOverlay={suppressLoadingOverlay}
        onGridReady={onGridReady}
        onColumnVisible={() => {
          if (pageName) onColumnVisible(pageName);
        }}
        onSelectionChanged={onSelectionChanged}
        getMainMenuItems={(p) => [
          'autoSizeThis',
          'autoSizeAll',
          'separator',
          {
            name: 'Size To Fit',
            action: () => {
              if (pageName) clearColumnsSize(pageName); // sizeColumnsToFit fires an event that is ignored by save handler if there are data in storage, so we clear stored data beforehand
              p.api?.sizeColumnsToFit();
            }
          },
          'separator',
          'resetColumns'
        ]}
        onFirstDataRendered={handleFirstDataRender}
        pagination
        suppressPaginationPanel
        suppressContextMenu
        domLayout={domLayout}
        suppressColumnVirtualisation={process.env.NODE_ENV === 'test'}
        rowBuffer={process.env.NODE_ENV === 'test' ? 999 : 10}
        rowSelection="multiple"
        groupSelectsChildren={groupSelectsChildren}
        suppressRowClickSelection={suppressRowClickSelection}
        processRowPostCreate={processRowPostCreate}
        isRowSelectable={isRowSelectable}
        tooltipShowDelay={0}
        isGroupOpenByDefault={isGroupOpenByDefault}
        components={{ ...components, groupCellRenderer: GroupCellRenderer, extendedGroupCellRenderer: GroupCellRendererExtended }}
        suppressAggFuncInHeader
        applyColumnDefOrder
        overlayNoRowsTemplate={overlayNoRowsTemplate}
      />
    </div>
  );
};
